import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { WebhookService } from 'services/webhooks.service';
import { Webhook } from 'utils/models/webhook.model';
import { RootState } from 'reducers/index';

interface WebhookDetail {
    data: Webhook;
    lastFetched: number;
}

interface WebhooksState {
    list: Webhook[];
    loadingList: boolean;

    details: Record<string, WebhookDetail>;
    loadingDetails: Record<string, boolean>;

    error?: string;
}

const initialState: WebhooksState = {
    list: [],
    loadingList: false,
    details: {},
    loadingDetails: {},
};

// ACTIONS

export const fetchWebhooks = createAsyncThunk(
    'webhooks/fetchList',
    async () => {
        const response = await WebhookService.getWebhooks();
        return response.data;
    }
);

export const fetchWebhookDetail = createAsyncThunk(
    'webhooks/fetchDetail',
    async (webhookId: string) => {
        const response = await WebhookService.getWebhook(webhookId);
        return {
            data: response.data,
            cached: false
        };
    }
);

// SLICE

const webhooksSlice = createSlice({
    name: 'webhooks',
    initialState,
    reducers: {
        invalidateWebhook: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            delete state.details[id];
        },
        setWebhookDetail: (state, action: PayloadAction<Webhook>) => {
            const data = action.payload;
            state.details[data.webhookId] = {
                data,
                lastFetched: Date.now()
            };
        },
        clearWebhooks: (state) => {
            state.list = [];
            state.details = {};
        }
    },
    extraReducers: (builder) => {
        // List
        builder.addCase(fetchWebhooks.pending, (state) => {
            state.loadingList = true;
            state.error = undefined;
        });
        builder.addCase(fetchWebhooks.fulfilled, (state, action) => {
            state.loadingList = false;
            state.list = action.payload;
        });
        builder.addCase(fetchWebhooks.rejected, (state, action) => {
            state.loadingList = false;
            state.error = action.error.message;
        });

        // Detail
        builder.addCase(fetchWebhookDetail.pending, (state, action) => {
            // Only set loading if not cached/already available to prevent flicker? 
            // Actually we want to know if an async fetch is happening.
            const id = action.meta.arg;
            state.loadingDetails[id] = true;
        });
        builder.addCase(fetchWebhookDetail.fulfilled, (state, action) => {
            const { data } = action.payload;
            const id = action.meta.arg;

            state.loadingDetails[id] = false;
            state.details[id] = {
                data,
                lastFetched: Date.now()
            };
        });
        builder.addCase(fetchWebhookDetail.rejected, (state, action) => {
            const id = action.meta.arg;
            state.loadingDetails[id] = false;
        });
    },
});

export const { invalidateWebhook, clearWebhooks, setWebhookDetail } = webhooksSlice.actions;

// SELECTORS
export const selectWebhookList = (state: RootState) => state.webhooks.list;
export const selectWebhookListLoading = (state: RootState) => state.webhooks.loadingList;

export const selectWebhookDetail = (id: string) => (state: RootState) => state.webhooks.details[id]?.data;
export const selectWebhookDetailLoading = (id: string) => (state: RootState) => !!state.webhooks.loadingDetails[id];

export default webhooksSlice.reducer;
